import { useEffect, useRef, useState } from "react";
import ProgressBar from "../ProgressBar";
import Button from "../ButtonV2";
import { EquityContribution } from "../EquityContribution";
import { RootState, useAppSelector } from "../../store/store";
import Loader from "../Loader";
import {
  useEntityInfoMutation,
  useGetEquityContributionDataMutation,
  useGetEquityContributionStatusQuery,
  useNewEntityStartedZohoMutation,
  useSubmitEquityContributionMutation,
} from "../../services/api";
import { useDispatch } from "react-redux";
import { setStage } from "../../store/reducers/stageReducer";
import { setSubmission } from "../../store/reducers/tileReducer";
import EquityModalNew from "./EquityModalNew";
import { tracker } from "../../services/mixpanel";
import { config } from "../../configs";
import EntitySetupPrompt from "./EntitySetupPrompt";

interface Props {
  isLoading: boolean;
  equityContributionLoader: boolean;
  setEquityContributionLoader: React.Dispatch<React.SetStateAction<boolean>>;
  apaData?: IApaDoc;
  refetchApa: any;
}

const Tile4 = ({
  isLoading,
  equityContributionLoader,
  setEquityContributionLoader,
  apaData,
  refetchApa,
}: Props) => {
  const dispatch = useDispatch();
  const stageData = useAppSelector((state: RootState) => state.stage);
  const entityInformationCollected = stageData.entityInformationCollected;
  const [equityResult, { isLoading: equityResultLoading }] =
    useEntityInfoMutation();
  const [equityContributionApi, { isLoading: equityContributionLoading }] =
    useSubmitEquityContributionMutation();
  const [openNoForm, setOpenNoForm] = useState(false);
  const [equityFormLoader, setEquityFormLoader] = useState(false);
  const [error, setError] = useState("");
  const [showEquityChart, setShowEquityChart] = useState(
    stageData.entityInformationCollected ?? false
  );
  const [isSoftCommitComplete, setIsSoftCommitComplete] = useState(
    stageData.softCommitComplete
  );
  const [equityContribution, setEquityContribution] = useState("");
  const [equityContributionSubmit, setEquityContributionSubmit] = useState("");
  const [showSignTile, setShowSignTile] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const EQUITY_CONTRIBUTION_FAQ_LINK =
    config.REACT_APP_EQUITY_CONTRIBUTION_FAQ_LINK;
  // const [zohoDataExists, setZohoDataExists] = useState<boolean | null>(null);
  const [currentYearBool, setCurrentYearBool] = useState<boolean>(false);
  const [equityContributionValue, setEquityContributionValue] = useState<number | null>(null);
  const zohoCustomerId = useAppSelector(
    (state: RootState) => state.login.zohoCustomerId
  );
  const [
    getEquityContributionData,
    { data: ecYearData, isLoading: ecLoading },
  ] = useGetEquityContributionDataMutation();

  const [entityStarted, { isLoading: entityStartedLoading }] =
      useNewEntityStartedZohoMutation();

  // ✅ Keep helper outside
  const fetchEquityData = async (
    zohoCustomerId: string | null,
    getEquityContributionData: any,
    setCurrentYearBool: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!zohoCustomerId) return;

    try {
      const result = await getEquityContributionData(zohoCustomerId).unwrap();

      const details = result.data?.data.details.output; // string from API
      if (details && details !== "{}") {
        const parsed = details;
        const currentYear = new Date().getFullYear();

        if (
          parsed?.OppYear === currentYear &&
          parsed?.["Equity Contribution"] !== null
        ) {
          setCurrentYearBool(true);
          setEquityContributionValue(parsed?.["Equity Contribution"]);
        }
      } 
    } catch (err) {
      setCurrentYearBool(false);
    }
  };

  useEffect(() => {
    fetchEquityData(
      zohoCustomerId,
      getEquityContributionData,
      setCurrentYearBool
    );
  }, [getEquityContributionData]);


  const { data: ecData, refetch: refetchEC } =
    useGetEquityContributionStatusQuery();

  const handleSubmit = (e: any) => {
    e.preventDefault();
  };

  const formatNumber = (number: string) => {
    const numericValue = parseInt(number.replace(/[^0-9]/g, ""));
    if (!isNaN(numericValue)) {
      return "$" + numericValue.toLocaleString();
    }
    return "";
  };

  const hasStarted = useRef(false);
  const handleInputChange = (event: any) => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      tracker.dashboard.equityContribution.formStarted();
    }
    const value = event.target.value.replace(/[^0-9.]/g, "");
    setEquityContribution(value);
  };

  const submitEquityData = async () => {
    setEquityContributionLoader(true);
    try {
      if (parseInt(equityContribution) < 50000) {
        setError("APA Document Minimum is $50,000");
        tracker.dashboard.equityContribution.formError();
        return;
      }
      setError("");
      const body = {
        equityContribution: equityContribution,
      };
      tracker.dashboard.equityContribution.formSubmitted();
      const data = (await equityContributionApi(body)) as Response<any>;
      if (data.data?.success) {
        setIsSoftCommitComplete(true);
        setEquityContributionSubmit(equityContribution);
        dispatch(
          setStage({
            ...stageData,
            softCommitComplete: true,
          })
        );
        dispatch(setSubmission({ component: "tile6", isSubmitted: false }));
        dispatch(setSubmission({ component: "tile5", isSubmitted: true }));
        setShowSignTile(true);
        refetchApa();
        fetchEquityData(
          zohoCustomerId,
          getEquityContributionData,
          setCurrentYearBool
        );
      }
    } catch (error) {
      console.error("There was a problem with your fetch operation:", error);
    } finally {
      setEquityContributionLoader(false);
    }
  };

  useEffect(() => {
    setShowEquityChart(stageData.entityInformationCollected ?? false);
  }, [stageData.entityInformationCollected]);

  if (currentYearBool && equityContributionValue !== null) {
    return null;
  }

  const startEntity = async (zohoId: string, retries = 3, delay = 2000) => {
    try {
      await entityStarted(zohoId).unwrap();
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => startEntity(zohoId, retries - 1, delay), delay);
      } else {
        console.error("Entity Started zoho failed:", err);
      }
    }
  };

  const handleClickNo = () => {
    startEntity(zohoCustomerId!);
    tracker.dashboard.equityContribution.noGoogleClick();
    window.open(config.REACT_APP_GOOGLE_NO_FORM_URL, "_blank");
  };

  return (
    <div className={`bg-[#FEFCFB] rounded-xl w-full pt-1 h-[390px] shadow-lg`}>
      <h2
        className="text-[25px] mb-[8px] p-1 text-black font-normal"
        style={{ height: "10%" }}
      >
        {showEquityChart || isSoftCommitComplete
          ? "Equity Contribution"
          : "Setup LLC"}
      </h2>
      <div className="flex flex-col items-center justify-center border-t-4 border-[#EFEFF4] h-[70%]">
        {showEquityChart ? (
          <>
            {apaData == undefined ||
            (apaData != undefined &&
              !apaData?.CAF_Signed &&
              !apaData?.MNDA_Signed &&
              !apaData?.OMA_Signed &&
              !apaData?.request_id) ||
            (apaData?.CAF_Signed &&
              apaData?.MNDA_Signed &&
              apaData?.OMA_Signed) ||
            isSoftCommitComplete ? (
              <form onSubmit={handleSubmit}>
                <div className={`mb-3 flex mt-4 justify-center`}>
                  <input
                    className={`w-full p-[2px] pl-[5px] border border-gray-400`}
                    placeholder="Enter your expected annual equity contribution"
                    type="text"
                    name="text"
                    autoComplete="off"
                    value={formatNumber(equityContribution)}
                    onChange={handleInputChange}
                  />
                  <button
                    className={`bg-[#3B755E] rounded-sm text-white ml-2 w-[7rem]`}
                    onClick={submitEquityData}
                    disabled={equityResultLoading}
                  >
                    {equityContributionLoader ? (
                      <Loader isLoading={true} size={12} />
                    ) : (
                      "SUBMIT"
                    )}
                  </button>
                </div>
                {error && (
                  <div className="text-red-500 text-sm text-center">
                    {error}
                  </div>
                )}
                {equityContributionLoader && (
                  <div className="text-green-600 text-md font-bold text-center mt-2">
                    Thanks for your submission. We will contact you when the
                    documents are ready to sign.
                  </div>
                )}
              </form>
            ) : (
              <EntitySetupPrompt
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                handleClickNo={handleClickNo}
                EQUITY_CONTRIBUTION_FAQ_LINK={
                  EQUITY_CONTRIBUTION_FAQ_LINK || ""
                }
              />
            )}

            {/* <div className={styles.tile2desp}>
        In order to meet this goal, your payment of 1/8 is due June 1.
      </div> */}
            {/* {equityResultLoading ? (
              <h1 className="text-white flex justify-center mt-12">
                <Button isLoading={isLoading} title="" variant="light4" />
              </h1>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="">
                  <ProgressBar value={25} />
                </div>
                <div className="mt-2">
                  <span className="text-3xl">
                    {apaData?.Total_Payment_Amount || "$Na"}
                  </span>
                  /{" "}
                  <span>
                    {equityContributionSubmit
                      ? formatNumber(equityContributionSubmit)
                      : "$Na"}
                  </span>
                </div>
              </div>
            )} */}
          </>
        ) : (
          <EntitySetupPrompt
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            handleClickNo={handleClickNo}
            EQUITY_CONTRIBUTION_FAQ_LINK={EQUITY_CONTRIBUTION_FAQ_LINK || ""}
          />
        )}
      </div>
    </div>
  );
};

export default Tile4;
